import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabase";
import type { ServiceItem } from "../../../mocks/services";

// 画像アップロードの簡易バリデーション。
// - 許可形式は JPG / PNG / WebP（UI 表記と一致）。
// - サイズ上限は 5MB（Storage バケットへ素の画像を入れる方針なので過大ファイルを弾く）。
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const SERVICE_IMAGES_BUCKET = "service-images";

interface ServiceFormModalProps {
  editing: ServiceItem | null;
  onClose: () => void;
  onSubmit: (data: Omit<ServiceItem, "id">) => void;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: 10,
  border: "1.5px solid rgba(44,95,79,0.2)",
  fontFamily: "'Noto Sans JP', sans-serif",
  fontSize: 14,
  color: "#1A1A1A",
  background: "#FDFAF3",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Noto Sans JP', sans-serif",
  fontSize: 13,
  fontWeight: 600,
  color: "#444444",
  display: "block",
  marginBottom: 6,
};

export default function ServiceFormModal({ editing, onClose, onSubmit }: ServiceFormModalProps) {
  const { t } = useTranslation("admin");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [detail, setDetail] = useState("");
  const [link, setLink] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setDescription(editing.description);
      setDetail(editing.detail);
      setLink(editing.link ?? "");
      setImageUrl(editing.imageUrl);
    } else {
      setName("");
      setDescription("");
      setDetail("");
      setLink("");
      setImageUrl("");
    }
    setUploadError("");
  }, [editing]);

  // 画像を Supabase Storage の 'service-images'（public read）へアップロードし、
  // 公開 URL を imageUrl に保存する。従来の base64 data URL 直保存は廃止。
  // 既存行が base64 でも表示は壊れない（<img src> は data: でも http(s): でも有効）。
  // 移行はフォーム再保存（＝この関数の実行）時に自然に Storage URL へ置き換わる。
  const handleFile = async (file: File) => {
    setUploadError("");

    // 簡易検証: 形式（JPG/PNG/WebP）とサイズ上限（5MB）。
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError(t("modal.imageTypeError"));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setUploadError(t("modal.imageSizeError"));
      return;
    }

    setUploading(true);
    try {
      // 衝突回避のためランダム＋時刻でパスを生成。拡張子は MIME から決定（パストラバーサル回避）。
      const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from(SERVICE_IMAGES_BUCKET)
        .upload(path, file, { cacheControl: "3600", contentType: file.type, upsert: false });
      if (uploadErr) {
        setUploadError(t("modal.imageUploadError"));
        return;
      }
      const { data } = supabase.storage.from(SERVICE_IMAGES_BUCKET).getPublicUrl(path);
      setImageUrl(data.publicUrl);
    } catch {
      setUploadError(t("modal.imageUploadError"));
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, description, detail, link: link || undefined, imageUrl });
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "40px 24px",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 560,
          padding: "36px 36px 32px",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              fontWeight: 700,
              fontSize: 18,
              color: "#1A1A1A",
            }}
          >
            {editing ? t("modal.editTitle") : t("modal.addTitle")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer flex items-center justify-center w-8 h-8"
            style={{
              borderRadius: 8,
              background: "rgba(0,0,0,0.05)",
              border: "none",
              color: "#555",
            }}
          >
            <i className="ri-close-line" style={{ fontSize: 18 }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div>
            <label htmlFor="svc-name" style={labelStyle}>
              {t("modal.serviceName")} <span style={{ color: "#dc2626" }}>{t("modal.requiredMark")}</span>
            </label>
            <input
              id="svc-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("modal.serviceNamePlaceholder")}
              required
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="svc-desc" style={labelStyle}>
              {t("modal.shortDescription")} <span style={{ color: "#dc2626" }}>{t("modal.requiredMark")}</span>
            </label>
            <input
              id="svc-desc"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("modal.shortDescriptionPlaceholder")}
              required
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="svc-detail" style={labelStyle}>
              {t("modal.detail")} <span style={{ color: "#dc2626" }}>{t("modal.requiredMark")}</span>
            </label>
            <textarea
              id="svc-detail"
              value={detail}
              onChange={(e) => {
                if (e.target.value.length <= 500) setDetail(e.target.value);
              }}
              placeholder={t("modal.detailPlaceholder")}
              required
              rows={5}
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.8 }}
            />
            <p
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontSize: 11,
                color: detail.length >= 480 ? "#dc2626" : "#aaaaaa",
                textAlign: "right",
                marginTop: 4,
              }}
            >
              {detail.length} / 500
            </p>
          </div>

          <div>
            <label htmlFor="svc-link" style={labelStyle}>
              {t("modal.linkUrl")}
            </label>
            <input
              id="svc-link"
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://..."
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("modal.image")} <span style={{ color: "#dc2626" }}>{t("modal.requiredMark")}</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {imageUrl ? (
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    borderRadius: 10,
                    overflow: "hidden",
                    border: "1.5px solid rgba(44,95,79,0.2)",
                  }}
                >
                  <img
                    src={imageUrl}
                    alt="preview"
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setImageUrl("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="cursor-pointer whitespace-nowrap"
                  style={{
                    marginTop: 8,
                    width: "100%",
                    padding: "8px 0",
                    borderRadius: 8,
                    border: "1.5px dashed rgba(44,95,79,0.3)",
                    background: "transparent",
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: 12,
                    color: "#888",
                    cursor: "pointer",
                  }}
                >
                  <i className="ri-refresh-line" style={{ marginRight: 4 }} />
                  {t("modal.changeImage")}
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                style={{
                  borderRadius: 10,
                  border: `2px dashed ${isDragging ? "#2C5F4F" : "rgba(44,95,79,0.25)"}`,
                  background: isDragging ? "rgba(44,95,79,0.04)" : "#FDFAF3",
                  padding: "32px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  transition: "border-color 0.2s, background 0.2s",
                }}
              >
                <div
                  className="flex items-center justify-center w-12 h-12"
                  style={{
                    borderRadius: "50%",
                    background: "rgba(44,95,79,0.08)",
                    color: "#2C5F4F",
                  }}
                >
                  <i className="ri-upload-cloud-2-line" style={{ fontSize: 24 }} />
                </div>
                <p
                  style={{
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#2C5F4F",
                    margin: 0,
                  }}
                >
                  {t("modal.clickToSelect")}
                </p>
                <p
                  style={{
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: 11,
                    color: "#aaaaaa",
                    margin: 0,
                  }}
                >
                  {t("modal.dragDrop")}
                </p>
              </div>
            )}

            {uploading && (
              <p
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: 12,
                  color: "#2C5F4F",
                  marginTop: 8,
                  marginBottom: 0,
                }}
              >
                <i className="ri-loader-4-line" style={{ marginRight: 4 }} />
                {t("modal.imageUploading")}
              </p>
            )}

            {uploadError && (
              <p
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: 12,
                  color: "#dc2626",
                  marginTop: 8,
                  marginBottom: 0,
                }}
              >
                {uploadError}
              </p>
            )}

            {/* required用の隠しinput */}
            <input
              type="text"
              value={imageUrl}
              onChange={() => {}}
              required
              tabIndex={-1}
              style={{
                opacity: 0,
                height: 0,
                padding: 0,
                border: "none",
                position: "absolute",
                pointerEvents: "none",
              }}
            />
          </div>

          <div className="flex gap-3 justify-end mt-2">
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap cursor-pointer"
              style={{
                padding: "10px 20px",
                borderRadius: 10,
                border: "1.5px solid rgba(44,95,79,0.2)",
                background: "transparent",
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                color: "#555555",
              }}
            >
              {t("modal.cancel")}
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="whitespace-nowrap cursor-pointer"
              style={{
                padding: "10px 24px",
                borderRadius: 10,
                border: "none",
                background: "#2C5F4F",
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 700,
                fontSize: 13,
                color: "#ffffff",
                transition: "opacity 0.2s",
                opacity: uploading ? 0.6 : 1,
                cursor: uploading ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!uploading) (e.currentTarget as HTMLButtonElement).style.opacity = "0.85";
              }}
              onMouseLeave={(e) => {
                if (!uploading) (e.currentTarget as HTMLButtonElement).style.opacity = "1";
              }}
            >
              {editing ? t("modal.update") : t("modal.add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
